import { Stack, Typography, Button, Collapse, buttonClasses, TextField, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useState, useRef, useEffect } from "react";
import { DIALOG_VARIANTS } from "@hyro-components/Dialogs/DialogConsts";
import Dialog from "@hyro-components/Dialogs/HyroUiDialog";
import useCallMeDialog from "./CallMeDialog.hook";
import { CALL_ME_FIELDS } from "@phrasing";
import HyroSingleSelect from "src/components/hyro-components/fields/single-option/HyroSingleSelect";
import ChevronDown from "src/icons/ChevronDown";
import Field from "@hyro-components/fields/HyroField";
import { FIELD_TYPES } from "@hyro-components/fields/utils";
import HyroRadioGroup from "@hyro-components/fields/radio-group/HyroRadioGroup";
import { useFlags } from "launchdarkly-react-client-sdk";
import { isOutboundAgentSelector } from "src/stores/selectors/appSelectors";
import useAppStore from "src/stores/appStore";

const CallMeDialog = ({ isOpen, onClose, callRequestSent, handleCallMe, hideBackdrop, PaperProps, variant = DIALOG_VARIANTS.transactional }) => {
  const { sites, errors, fields, disableCall, loading, handleChange, fieldsForServer } = useCallMeDialog();
  const [showAdvanced, setShowAdvanced] = useState(false);
  const isOutboundAgent = useAppStore(isOutboundAgentSelector);
  const { showCallMeOutboundJson } = useFlags();
  const endOfAdvancedRef = useRef(null);
  const theme = useTheme();

  useEffect(() => {
    if (showAdvanced && endOfAdvancedRef.current) {
      setTimeout(() => {
        endOfAdvancedRef.current.scrollIntoView({ behavior: "smooth" });
      }, 300); // Small delay to allow Collapse animation to start
    }
  }, [showAdvanced]);

  return (
    <Dialog
      showCloseButton={variant === DIALOG_VARIANTS.transactional}
      testId="call-me-dialog"
      title="Test your Hyro AI Agent"
      open={isOpen}
      hideBackdrop={hideBackdrop}
      variant={variant}
      handleSubmit={() => handleCallMe(fieldsForServer)}
      handleCancel={onClose}
      handleClose={onClose}
      submitButtonLoading={callRequestSent}
      disableSave={!!disableCall}
      submitButtonText="Call me"
      PaperProps={PaperProps}
    >
      <DialogContent>
        <InstructionsBox>
          <InstructionsText>Please complete the following details to receive a phone call from your Hyro AI Agent.</InstructionsText>
        </InstructionsBox>
        <Stack gap={3}>
          <InputField
            errorColor={theme.palette.error[500]}
            testId="phone-number-field"
            disabled={callRequestSent}
            field={CALL_ME_FIELDS.PHONE_NUMBER} placeholder={CALL_ME_FIELDS.PHONE_NUMBER.PLACEHOLDER}
            errors={errors} value={fields[CALL_ME_FIELDS.PHONE_NUMBER.ID]} mandatory onChange={handleChange}
            onEnterPress={() => handleCallMe(fieldsForServer)}
          />

          {isOutboundAgent && showCallMeOutboundJson ? <Stack>
            <Typography variant="subtitle2" color="text.800">{CALL_ME_FIELDS.OUTBOUND_JSON.LABEL} <span style={{ color: theme.palette.error[500] }}> *</span></Typography>
            <Typography sx={{ mb: 2 }} variant="body2"
              color="text.800">{CALL_ME_FIELDS.OUTBOUND_JSON.INSTRUCTIONS}</Typography>
            <TextField
              multiline
              rows={3}
              error={!!errors[CALL_ME_FIELDS.OUTBOUND_JSON.ID]}
              helperText={errors[CALL_ME_FIELDS.OUTBOUND_JSON.ID]}
              value={fields[CALL_ME_FIELDS.OUTBOUND_JSON.ID]}
              onChange={(e) => handleChange({ target: { name: CALL_ME_FIELDS.OUTBOUND_JSON.ID, value: e.target.value } })}
              placeholder={CALL_ME_FIELDS.OUTBOUND_JSON.PLACEHOLDER}
            />
          </Stack> : null}

          <StyledAdvancedButton
            data-testid="advanced-options-button"
            variant="clear"
            endIcon={<ChevronDown stroke="#4548C4" style={{
              width: "18px",
              transform: showAdvanced ? "rotate(180deg)" : ""
            }} />}
            onClick={() => setShowAdvanced(prev => !prev)}
          >
            Advanced Options
          </StyledAdvancedButton>

          <Collapse in={showAdvanced}>
            <Stack gap={3}>
              <InputField
                errorColor={theme.palette.error[500]}
                testId="caller-id-field"
                disabled={callRequestSent}
                field={CALL_ME_FIELDS.CALLER_ID} placeholder={CALL_ME_FIELDS.CALLER_ID.PLACEHOLDER} errors={errors}
                value={fields[CALL_ME_FIELDS.CALLER_ID.ID]} onChange={handleChange}
                onEnterPress={() => handleCallMe(fieldsForServer)}
              />

              {!isOutboundAgent ? <InputField
                errorColor={theme.palette.error[500]}
                testId="sites-field"
                disabled={callRequestSent}
                type='select'
                field={CALL_ME_FIELDS.SITES}
                placeholder={CALL_ME_FIELDS.SITES.PLACEHOLDER}
                errors={errors}
                options={sites}
                value={fields[CALL_ME_FIELDS.SITES.ID]}
                onChange={handleChange}
                loading={loading}
              /> : null}

              {!isOutboundAgent ? <Stack>
                <Typography variant="subtitle2" color="text.800">{CALL_ME_FIELDS.OPERATING_HOURS.LABEL}</Typography>
                <Typography sx={{ mb: 2 }} variant="body2"
                  color="text.800">{CALL_ME_FIELDS.OPERATING_HOURS.INSTRUCTIONS}</Typography>
                <HyroRadioGroup
                  value={fields[CALL_ME_FIELDS.OPERATING_HOURS.ID]}
                  onChange={(e) => handleChange({ target: { name: CALL_ME_FIELDS.OPERATING_HOURS.ID, value: e === "true" } })}
                  options={[
                    { key: true, label: "On" },
                    { key: false, label: "Off" }
                  ]} />
              </Stack> : null}

              <div ref={endOfAdvancedRef} data-testid="end-of-advanced-section" />
            </Stack>
          </Collapse>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

const InputField = ({ field, type = "text", value, placeholder, errors, onChange, options, loading, disabled, mandatory = false, testId, onEnterPress, errorColor }) => <Stack>
  <Stack sx={{ mb: 2 }}>
    <Typography variant="subtitle2" color="text.800">
      {field.LABEL} {mandatory && <span style={{ color: "#EF404B" }}> *</span>}
    </Typography>

    {field.INSTRUCTIONS && <Typography variant="body2" color="text.800">
      {field.INSTRUCTIONS}
    </Typography>}
  </Stack>

  {type === "select" ?
    <HyroSingleSelect
      testId={testId}
      disabled={disabled}
      placeholder={"Select a site"}
      loading={loading}
      error={!!errors[field.ID]}
      onChange={(value) => onChange({ target: { name: CALL_ME_FIELDS.SITES.ID, value } })}
      label={field.LABEL}
      options={options}
      value={value} /> :
    <Field
      fieldType={FIELD_TYPES.PHONE}
      testId={testId}
      disabled={disabled}
      name={field.ID}
      label={""}
      placeholder={placeholder}
      error={errors[field.ID]}
      value={value}
      onChange={val => onChange({ target: { name: field.ID, value: val } })}
      onKeyDown={(event) => {
        if (event.key === "Enter" && !Object.values(errors).some(Boolean) && onEnterPress) {
          event.preventDefault();
          onEnterPress();
        }
      }}
    />}

</Stack >;

const DialogContent = styled("div")(() => ({
  display: "flex",
  flexFlow: "column"
}));

const InstructionsText = styled("div")(({ theme }) => ({
  fontSize: "16px",
  maxWidth: "475px",
  lineHeight: "24px",
  color: theme.palette.text[800]
}));

const InstructionsBox = styled("div")(() => ({
  marginTop: "8px",
  marginBottom: "24px"
}));

const StyledAdvancedButton = styled(Button)(({ theme }) => ({
  alignSelf: "flex-start",
  padding: "6px 12px",
  color: theme.palette.primary.main,
  fontSize: "14px",
  [`& .${buttonClasses.endIcon}`]: {
    marginLeft: "4px"
  }
}));

export default CallMeDialog;
